import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { UntilDestroy } from '@ngneat/until-destroy';
import { Subject } from 'rxjs';
import { skip, takeUntil } from 'rxjs/operators';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';

import { LocalStorageService } from 'src/app/services/local-storage.service';
import { MapService } from 'src/app/services/map.service';
import { ModuleService } from 'src/app/services/module.service';
import { PlotCadastreSolaireIndicatorService } from 'src/app/services/plotIndicator/plot-cadastre-solaire.service';
import { SolarService } from 'src/app/services/cadastre-solaire/cadastre-solaire.service';
import { SolarPvSurplusService } from 'src/app/services/cadastre-solaire/cadastre-solaire-pv-surplus.service';
import { SolarPvTotalityService } from 'src/app/services/cadastre-solaire/cadastre-solaire-pv-totality.service';
import { SolarThermalService } from 'src/app/services/cadastre-solaire/cadastre-solaire-thermal.service';
import { UsefulService } from 'src/app/services/UsefulService';

import { ConfirmationModalComponent } from '../../../../modals/confirmation/confirmation-modal.component';
import { Indicator } from 'src/app/models/Indicator';

@UntilDestroy()
@Component({
    selector: 'cadastreSolaireInit',
    templateUrl: './cadastre-solaire-init.template.html',
    styleUrls: ['./cadastre-solaire-init.component.scss'],
    standalone: false,
})
export class CadastreSolaireInitComponent implements OnInit, OnDestroy {
    private destroy$ = new Subject<void>();

    private showMobileUi: boolean = false;

    public showLoader: boolean = true;

    public selectedRoofTitle: string;

    private customInfo: any;
    private pvModuleArea: any;

    public selectedElements: any[];

    public isAnyZoneConstraint: boolean = true;
    public isConstraintZone = {
        architectural: true,
        nature: true,
        altitude: true,
        coastal: true,
    };

    public indicatorPlot: Indicator;
    public distributions: any[] = [];
    private goodPotentialOpacity: number = 1;
    private badPotentialOpacity: number = 0.35;

    constructor(
        private notification: ToastrService,
        private modalService: NgbModal,
        @Inject(LocalStorageService) private localStorageService: LocalStorageService,
        @Inject(MapService) private mapService: MapService,
        @Inject(ModuleService) public moduleService: ModuleService,
        @Inject(PlotCadastreSolaireIndicatorService)
        private plotCadastreSolaireIndicatorService: PlotCadastreSolaireIndicatorService,
        @Inject(SolarPvTotalityService) private solarPvTotalityService: SolarPvTotalityService,
        @Inject(SolarPvSurplusService) private solarPvSurplusService: SolarPvSurplusService,
        @Inject(SolarService) public solarService: SolarService,
        @Inject(SolarThermalService) public solarThermalService: SolarThermalService,
        @Inject(UsefulService) private usefulService: UsefulService,
    ) {}

    async ngOnInit(): Promise<void> {
        this.showMobileUi = this.usefulService.showMobileUi;

        this.isAnyZoneConstraint = this.solarService.isAnyZoneConstraint;
        this.isConstraintZone = this.solarService.isConstraintZone;
        this.selectedElements = JSON.parse(JSON.stringify(this.solarService.selectedElements));
        this.showLoader = this.selectedElements.length > 0;

        if (!this.selectedElements.length) {
            this.moduleService.closeModule('solar');
            this.solarService.updateMainStage(null);
            return;
        }

        if (this.selectedElements.length == 1) {
            await this._getCustomTerritoryInfo();
        }

        if (this.solarService.results.length == 0) {
            this._initSelectionData();
        }

        this._updateInfo();
        this.showLoader = false;

        this._listenToEvents();
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }

    private _listenToEvents() {
        this.solarService.selectedElementsObs$
            .pipe(skip(1), takeUntil(this.destroy$))
            .subscribe(async (selectedElements) => {
                this.showLoader = selectedElements.length > 0;
                this._initSelectionData();

                if (!selectedElements.length) {
                    this.moduleService.closeModule('solar');
                    this.solarService.updateMainStage(null);
                    return;
                }

                this._updateInfo();

                this.isAnyZoneConstraint = this.solarService.isAnyZoneConstraint;
                this.selectedElements = JSON.parse(JSON.stringify(selectedElements));
                this.showLoader = false;
            });
    }

    private async _getCustomTerritoryInfo() {
        const isDifferentTerritory = this.solarService.isDifferentTerritory();
        if (!isDifferentTerritory) {
            return;
        }

        try {
            const customInfo = await this.solarService.getPvParameters();
            this._setCustomInfo(customInfo);
        } catch (error) {
            console.error('Error _getCustomTerritoryInfo', error);
            this.solarService.selectionInfo.showLoader = false;
            this.showLoader = false;
            this.solarService.customInfo = undefined;
            this.plotCadastreSolaireIndicatorService.selectedExistingElementsGroup.clearLayers();
            this.plotCadastreSolaireIndicatorService.selectedNewElementsGroup.clearLayers();
            this.solarService.updateMainStage(null);
            this.moduleService.closeModule('solar');
            this.notification.error(
                "Une erreur est survenue. Impossible d'initialiser les paramètres.",
            );
            throw error;
        }
    }

    private _setCustomInfo(customInfo: any) {
        this.customInfo = customInfo;
        this.solarService.customInfo = customInfo;

        this.pvModuleArea = this.customInfo.pv.parameter.surface_module;
    }

    removeElement(elementId: number) {
        const isAnyResultDefined = this.solarService.results.length > 0;
        const layer = this.solarService.indicatorPlot.findLayerBySiterreId(elementId);

        if (isAnyResultDefined) {
            this._openReinitSimulation(layer);
        } else {
            this._removeExistingLayer(layer);
        }
    }

    private _openReinitSimulation(layer: L.Layer) {
        const confirmationMessage = [
            'Une simulation de projet solaire est en cours.',
            'Sélectionner une autre toiture mettra fin à cette simulation.',
            'Voulez-vous continuer ?',
        ];

        const modalRef = this.modalService.open(ConfirmationModalComponent);
        modalRef.componentInstance.header = "Réinitialiser l'étude";
        modalRef.componentInstance.body = confirmationMessage.join('<br/>');
        modalRef.componentInstance.validateLabel = 'Confirmer';
        modalRef.result.then(
            (result) => this._removeExistingLayer(layer),
            (reason) => {},
        );
    }

    private _removeExistingLayer(layer: L.Layer) {
        this.plotCadastreSolaireIndicatorService.selectedExistingElementsGroup.removeLayer(layer);
    }

    private _updateInfo() {
        if (this.solarService.selectedElements.length) {
            this._instanciateSolarPv();
            this._setSelectionInfo();
            this._setSelectedRoofTitle();

            this.indicatorPlot = this.solarService.indicatorPlot;
            const isRoofSection =
                this.indicatorPlot.plugin == 'solaire' &&
                this.indicatorPlot.vector_base == 'solaire';

            this.distributions = [];

            if (isRoofSection) {
                this._setRoofSectionDistribution(3, this.goodPotentialOpacity);
                this._setRoofSectionDistribution(2, this.goodPotentialOpacity);
                this._setRoofSectionDistribution(1, this.badPotentialOpacity);
                this._setRoofSectionDistribution(0, this.badPotentialOpacity);
            } else {
                this._setBuildingDistribution(3, this.goodPotentialOpacity);
                this._setBuildingDistribution(2, this.goodPotentialOpacity);
                this._setBuildingDistribution(1, this.badPotentialOpacity);
                this._setBuildingDistribution(0, this.badPotentialOpacity);
            }
            this.solarService.distributions = this.distributions;
        }
    }

    private _setRoofSectionDistribution(potentialId: number, opacity: number) {
        const total = this.solarPvTotalityService.totalModuleArea;
        const selectedElements = this.solarService.selectedElements;
        const elements = selectedElements.filter((element) =>
            element.solarPotentials.some(
                (solarPotential: any) => solarPotential.potential_id == potentialId,
            ),
        );

        if (elements.length) {
            const value = elements.reduce((sum, element) => sum + element.surface_disponible, 0);
            const stringifiedValue = this.usefulService.stringifyNumber(value);

            const distribution = {
                label: elements[0].solarPotentials[0].label,
                color: elements[0].solarPotentials[0].color,
                value: stringifiedValue,
                width: `${this.usefulService.round((value / total) * 100)}%`,
                opacity: opacity,
            };
            this.distributions.push(distribution);
        }
    }

    private _setBuildingDistribution(potentialId: number, opacity: number) {
        const selectedElements = this.solarService.selectedElements;

        const total = selectedElements.reduce(
            (sum, element) =>
                sum +
                (element.solarPotentials?.reduce(
                    (innerSum, potential) => innerSum + potential.surface_disponible,
                    0,
                ) ?? 0),
            0,
        );

        let value = 0;
        let color = '';
        let label = '';

        selectedElements.forEach(({ surface_disponible, usableRoofArea, solarPotentials }) => {
            const potential = solarPotentials.find((p) => p.potential_id === potentialId);
            if (!potential) return;

            const ratio = usableRoofArea / surface_disponible;
            value += potential.surface_disponible * ratio;
            color = potential.color;
            label = potential.label;
        });

        if (!value) return;

        const roundedValue = this.usefulService.round(value);
        const stringifiedValue = this.usefulService.stringifyNumber(roundedValue);

        this.distributions.push({
            label,
            color,
            value: stringifiedValue,
            width: `${this.usefulService.round((value / total) * 100)}%`,
            opacity,
        });
    }

    private _instanciateSolarPv() {
        this.solarPvTotalityService.init(this.solarService.selectedElements, this.pvModuleArea);
        this.solarPvTotalityService.calcByElement();
        this.solarPvTotalityService.calcTotal();
    }

    private _setSelectionInfo() {
        const isRoofSection = this.solarService.indicatorPlot!.vector_base == 'solaire';

        const totalModuleAreaRounded = this.usefulService.round(
            this.solarPvTotalityService.totalModuleArea,
        );
        const totalModuleArea = this.usefulService.stringifyNumber(totalModuleAreaRounded);

        const totalGrossRoofAreaRounded = this.usefulService.round(
            this.solarPvTotalityService.totalGrossRoofArea,
        );
        const totalGrossRoofArea = this.usefulService.stringifyNumber(totalGrossRoofAreaRounded);

        let myRoofLabel = '';
        let directionLabel = '';
        let tiltLabel = '';

        if (this.solarService.selectedElements.length === 1) {
            const element = this.solarService.selectedElements[0];
            const elementTypeLabel = isRoofSection ? 'pan de toiture' : 'toiture';

            myRoofLabel = `Votre ${elementTypeLabel} peut recevoir jusqu'à`;

            const direction = element.directionDetail;
            const tilt = this.usefulService.round(element.tilt);
            const isFlat = element.toit_plat;
            if (isFlat) {
                directionLabel = 'de surface';
                tiltLabel = 'en toit plat.';
            } else {
                const tiltStringified = this.usefulService.stringifyNumber(tilt);
                directionLabel = `de surface principalement orientée ${direction},`;
                tiltLabel = `inclinée à ${tiltStringified}°.`;
            }
        } else {
            const elementTypeLabel = isRoofSection ? 'pans de toiture' : 'toitures';
            myRoofLabel = `Vos ${elementTypeLabel} peuvent recevoir jusqu'à`;
            directionLabel = 'de surface.';
        }

        this.solarService.initSelectionInfo();
        this.solarService.selectionInfo.myRoof = myRoofLabel;
        this.solarService.selectionInfo.totalModuleArea = totalModuleArea;
        this.solarService.selectionInfo.totalGrossRoofArea = totalGrossRoofArea;
        this.solarService.selectionInfo.direction = directionLabel;
        this.solarService.selectionInfo.tilt = tiltLabel;
        this.solarService.selectionInfo.showLoader = false;
    }

    findElementOnMap(elementId: string) {
        const layer = this.solarService.indicatorPlot.findLayerBySiterreId(elementId);
        const latLng = layer.getCenter();

        if (this.showMobileUi) {
            this.moduleService.closeModule('solar');
        }
        this._targetElement(elementId);
        this.mapService.invalidateSize();
        this.plotCadastreSolaireIndicatorService.centerMapOnLayer(latLng);
    }

    private _targetElement(elementId: string) {
        this.mapService.closeAllPopup();
        const isAnyResultDefined = this.solarService.results.length > 0;

        this.solarService.indicatorPlot.openPopup = true;
        this.solarService.indicatorPlot.targetElement(elementId, false);
        this.solarService.indicatorPlot.openPopup = !isAnyResultDefined;
    }

    startSimulation() {
        this.solarService.updateMainStage('profileStage');
    }

    goToStage(stage: string) {
        this.solarService.updateMainStage(stage);
    }

    private _setSelectedRoofTitle(): void {
        const isRoofSection = this.solarService.indicatorPlot.vector_base == 'solaire';
        this.selectedRoofTitle = isRoofSection
            ? 'Pans de toitures sélectionnés :'
            : 'Toitures sélectionnées :';
    }

    private _initSelectionData() {
        this.solarService.initProfile();
        this.solarService.initDistributions();
        this.solarService.initResults();
        this.solarPvSurplusService.initTypologyData();
        this.solarPvSurplusService.initEquipmentsData();
        this.solarPvSurplusService.initElectricMobilityData();
        this.solarPvSurplusService.initConsumptionData();
        this.solarPvSurplusService.initImportedElectricLoad();
        this.solarPvSurplusService.initEquipments();
    }
}
